import React, { useState } from 'react'
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material'
import { Email as EmailIcon, Person as PersonIcon } from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../../lib/api'
import SEOHead from '../../../shared/components/SEOHead'

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await api.forgotPassword(email.trim() || username.trim())
      setSuccess(true)
    } catch (err: any) {
      console.error('Password reset request failed:', err)
      setError(
        err.message || 
        t('forgotPassword.error', 'Failed to send password reset email. Please try again.')
      )
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <SEOHead
          title={t('forgotPassword.title', 'Passwort vergessen')}
          description={t('forgotPassword.description', 'Passwort zurücksetzen')}
        />
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('forgotPassword.successMessage', 'Wenn ein Konto mit dieser E-Mail-Adresse oder diesem Benutzernamen existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet. Bitte überprüfen Sie Ihr E-Mail-Postfach.')}
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              color="primary"
            >
              {t('forgotPassword.backToHome', 'Zur Startseite')}
            </Button>
          </Box>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <SEOHead
        title={t('forgotPassword.title', 'Passwort vergessen')}
        description={t('forgotPassword.description', 'Passwort zurücksetzen')}
      />
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {t('forgotPassword.title', 'Passwort vergessen')}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {t('forgotPassword.description', 'Geben Sie Ihre E-Mail-Adresse oder Ihren Benutzernamen ein, um einen Link zum Zurücksetzen Ihres Passworts zu erhalten.')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('forgotPassword.emailLabel', 'E-Mail-Adresse')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required={!username}
            disabled={loading || !!username}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" align="center" sx={{ my: 2, color: 'text.secondary' }}>
            {t('forgotPassword.or', 'oder')}
          </Typography>

          <TextField
            fullWidth
            label={t('forgotPassword.usernameLabel', 'Benutzername')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required={!email}
            disabled={loading || !!email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading || (!email && !username)}
            sx={{ mb: 2 }}
          >
            {loading
              ? t('forgotPassword.sending', 'Wird gesendet...')
              : t('forgotPassword.sendResetLink', 'Link zum Zurücksetzen senden')}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <MuiLink component={RouterLink} to="/" color="primary">
              {t('forgotPassword.backToHome', 'Zur Startseite')}
            </MuiLink>
          </Box>
        </Box>
      </Paper>
    </Container>
  )
}

export default ForgotPassword

